import logger from './logger';
import ReactDOMServer from 'react-dom/server';
import store from '../redux/store';
import { cloneDeep } from 'lodash-es';
import { LATITUDE_LONGITUDE_REGEX, US_MOBILE_REGEX, US_ZIP_REGEX } from '../constants/regex';
import { ROLE } from '../constants/profile';
import {
  Address,
  CompanyDetails,
  CompanyInfoDetails,
  CompanyLocation,
  LoadingStatus,
  NotificationType,
  Range,
  RoleToSecurityLevelId,
  UserDetails,
  UserInvitation,
  ZipCodeType,
} from '../types/profile';

type GetRoleOptions = {
  isOwner?: boolean;
  isPendingUser?: boolean;
};

/**
 * Formats the name by concatenating the first name and last name.
 * @param firstName The first name.
 * @param lastName The last name.
 * @returns The formatted full name.
 */
export const getFormattedName = (
  firstName: string | null = null,
  lastName: string | null = null,
) => {
  let name = ``;
  if (firstName) name += firstName;
  if (lastName) name += ` ${lastName}`;
  return name;
};

/**
 * Formats the address by concatenating its parts.
 * @param street1 The first line of the street address.
 * @param street2 The second line of the street address.
 * @param city The city name.
 * @param state The state name.
 * @param zipCode The ZIP code.
 * @returns The formatted address string.
 */
export const getFormattedAddress = (
  street1: string | null = null,
  street2: string | null = null,
  city: string | null = null,
  state: string | null = null,
  zipCode: string | number | null = null,
) => {
  let address = ``;
  if (street1) address += `${street1}`;
  if (street2) address += `, ${street2}`;
  if (city) address += `, ${city}`;
  if (state) address += `, ${state}`;
  if (zipCode) address += `, ${zipCode}`;
  return address;
};

/**
 * Splits a full name into first name and last name.
 * @param name The full name to split.
 * @returns An object containing firstName and lastName.
 */
export const splitname = (name: string | null) => {
  if (!name) {
    return {
      firstName: '',
      lastName: '',
    };
  }
  name = name.trim();

  // Split the full name into parts using spaces
  const nameParts = name.split(/\s+/);

  let firstName = '';
  let lastName = '';

  if (nameParts.length === 1) {
    firstName = nameParts[0];
  } else if (nameParts.length >= 2) {
    firstName = nameParts[0];
    lastName = nameParts.slice(1).join(' ');
  }

  return {
    firstName: firstName,
    lastName: lastName,
  };
};

/**
 * Splits an address string into its components.
 * @param address The address string to split.
 * @returns An object containing street1, street2, city, state, and zipCode.
 */
export const splitAddress = (address: string | null) => {
  if (!address) {
    return {
      street1: ``,
      street2: ``,
      city: ``,
      state: ``,
      zipCode: 0,
    };
  }
  const parts = address.split(',');

  const trimmedParts = parts.map((part) => part.trim());

  let street1, street2, city, state, zipCode;

  if (trimmedParts.length === 5) {
    street1 = trimmedParts[0];
    street2 = trimmedParts[1];
    city = trimmedParts[2];
    state = trimmedParts[3];

    zipCode = parseInt(trimmedParts.slice(4).join(',').trim(), 10);
  } else if (trimmedParts.length === 4) {
    street1 = trimmedParts[0];
    city = trimmedParts[1];
    state = trimmedParts[2];
    street2 = '';
    zipCode = parseInt(trimmedParts.slice(3).join(',').trim(), 10);
  } else if (trimmedParts.length === 2) {
    street1 = trimmedParts[0];
    street2 = trimmedParts[1];
    city = '';
    state = '';
    zipCode = 0;
  } else if (trimmedParts.length === 1) {
    street1 = trimmedParts[0];
    street2 = '';
    city = '';
    state = '';
    zipCode = 0;
  } else {
    // Default case if no valid parts are found
    street1 = '';
    street2 = '';
    state = '';
    city = '';
    zipCode = 0;
  }

  return {
    street1: street1,
    street2: street2,
    city: city,
    state: state,
    zipCode: zipCode,
  };
};

/**
 * Validates a US ZIP code format.
 * @param zip The ZIP code to validate.
 * @returns An object indicating if the ZIP code is valid and its type.
 */
export const validateZip = (zip: string) => {
  // Regular expression to match a valid US zip code with optional 4-digit extension
  const zipRegex = US_ZIP_REGEX;

  // Test the zip code against the regex pattern
  const match = zipRegex.exec(zip);

  if (!match) {
    return { valid: false, type: null };
  }

  const [, fiveDigitPart, fourDigitPart] = match;

  if (fiveDigitPart && !fourDigitPart) {
    return { valid: true, type: ZipCodeType.STANDARD };
  } else if (fiveDigitPart && fourDigitPart) {
    return { valid: true, type: ZipCodeType.EXTENDED };
  } else {
    return { valid: false, type: null };
  }
};

/**
 * Checks if the given phone number string is a valid US mobile number.
 * @param phoneNumber The phone number string to validate.
 * @returns A boolean indicating whether the phone number is valid as per US mobile number standards.
 */
export const isValidUSMobileNumber = (phoneNumber: string) => {
  const regex = US_MOBILE_REGEX;

  return regex.test(phoneNumber);
};

/**
 * Checks if latitude and longitude values are within valid ranges.
 * @param latitude The latitude value.
 * @param longitude The longitude value.
 * @returns A boolean indicating if coordinates are valid.
 */
const checkLatitudeLongitudeRange = (latitude: number, longitude: number): boolean => {
  const isLatitudeValid = latitude >= -90 && latitude <= 90;
  const isLongitudeValid = longitude >= -180 && longitude <= 180;
  return isLatitudeValid && isLongitudeValid;
};
/**
 * Validates and extracts latitude and longitude from a string in "latitude,longitude" format.
 * @param coordStr The string containing latitude and longitude.
 * @returns An object indicating if coordinates are valid, along with latitude and longitude values.
 */
export const isValidCoordinates = (coordStr: string) => {
  // Regular expression to match latitude,longitude format
  const coordRegex = LATITUDE_LONGITUDE_REGEX;

  // Test the input string against the regex pattern
  const match = coordRegex.exec(coordStr);

  if (!match) {
    logger('error', `invalid coordinates ${coordStr}`);
    return { valid: false, latitude: null, longitude: null };
  }

  const latStr = match[match.length - 2];
  const lonStr = match[match.length - 1];
  const latitude = parseFloat(latStr);
  const longitude = parseFloat(lonStr);

  // Check if latitude and longitude are within valid ranges
  if (!checkLatitudeLongitudeRange(latitude, longitude)) {
    return { valid: false, latitude: null, longitude: null };
  }

  return { valid: true, latitude, longitude };
};

/**
 * Finds the notification type ID by name from a list of notification types.
 * @param notificationTypes An array of NotificationType objects.
 * @param name The name of the notification type to find.
 * @returns The notification type ID if found, otherwise null.
 */
export const findNotificationTypeId = (
  notificationTypes: NotificationType[] | null,
  name: string,
) => {
  const notificationType = notificationTypes?.find(
    (notificationType) => notificationType.name === name,
  );
  return notificationType ? notificationType.notification_type_id : null;
};

/**
 * Converts an SVG component to a data URL.
 * @param svgComponent The JSX element representing the SVG.
 * @returns The data URL string.
 */
export const svgToDataUrl = (svgComponent: JSX.Element) => {
  const svgString = ReactDOMServer.renderToStaticMarkup(svgComponent);
  const svgEncoded = encodeURIComponent(svgString);
  return `url("data:image/svg+xml,${svgEncoded}")`;
};

/**
 * Constructs a company details object based on the primary billing contact user.
 * @param primaryBillingContactUser The primary billing contact user details.
 * @returns An object containing detailed company information.
 */
export const constructCompanyDetails = (company: CompanyDetails) => {
  const companyName = company.name;
  const companyPhone = company.authorized_signer.phone_number;
  const billingLocation = company.billing_location;

  let companyAddress: Address | null = null;
  if (billingLocation) {
    companyAddress = {
      street1: billingLocation.street_1,
      city: billingLocation.city,
      state: billingLocation.state.name,
      zip: billingLocation.zip_code.toString().padStart(5, '0'),
    };
  }

  const companyDetails: CompanyInfoDetails = {
    name: companyName,
    address: companyAddress,
    phone: companyPhone,
    billingLocationId: billingLocation?.location_id ?? null,
    ownerId: company.owner_user_id,
  };

  return companyDetails;
};

/**
 * Formats a value is empty or null.
 * @param value The value.
 * @returns A formatted string value.
 */
export const formattedValue = (value: unknown) => {
  if (!value) return '--';
  return value as string;
};

export const checkInRange = (value: number, range: Range) => {
  return value >= range.min && value <= range.max;
};

export const isWithinRanges = (value: number, ranges: Range[]) => {
  return ranges.some((range) => checkInRange(value, range));
};

export const getRoleDetail = (securityLevelId: number, options?: GetRoleOptions) => {
  if (options?.isOwner) {
    return ROLE.FULL_ACCESS;
  }

  let roleName = '';
  switch (securityLevelId) {
    case RoleToSecurityLevelId.ADMIN:
      roleName = ROLE.FULL_ACCESS;
      break;

    case RoleToSecurityLevelId.MANAGER:
      roleName = ROLE.MANAGER;
      break;

    case RoleToSecurityLevelId.PENDING:
      roleName = ROLE.PENDING;
      break;

    case RoleToSecurityLevelId.SALESPERSON:
      roleName = ROLE.SALESPERSON;
      break;

    case RoleToSecurityLevelId.USER:
      roleName = ROLE.USER;
      break;

    case RoleToSecurityLevelId.OPERATIONS:
      roleName = ROLE.OPERATIONS;
      break;

    case RoleToSecurityLevelId.DRIVER:
      roleName = ROLE.DRIVER;
      break;

    default:
      return '';
  }
  if (options?.isPendingUser) {
    roleName += ROLE.PENDING_USER_SUFFIX;
  }

  return roleName;
};

export const isUserOwner = (user: UserDetails, companyDetails: CompanyInfoDetails | null) => {
  return user.user_id === companyDetails?.ownerId;
};

export const getUserRoleLabel = (user: UserDetails, companyDetails: CompanyInfoDetails | null) => {
  const isOwner = isUserOwner(user, companyDetails);
  const roleLabel = getRoleDetail(user.security_level_id, { isOwner });
  return formattedValue(roleLabel);
};

export const getPendingUserRoleLabel = (user: UserInvitation) => {
  const roleLabel = getRoleDetail(user.security_level_id, { isPendingUser: true });
  return formattedValue(roleLabel);
};

export const shouldDataBeFetched = (loadingStatus: LoadingStatus) => {
  return loadingStatus !== LoadingStatus.SUCCEEDED && loadingStatus !== LoadingStatus.IN_PROGRESS;
};

export const isIndividualAccount = () => {
  const authState = store.getState().auth.authContext;
  return authState?.email === authState?.companyName;
};

export const sortJobsiteLocations = (
  locations: CompanyLocation[] | null,
  preferredJobsiteLocationId: number | null | undefined,
) => {
  if (!locations) return [];
  if (!preferredJobsiteLocationId) return locations;

  return cloneDeep(locations).sort((a, b) => {
    if (a.location_id === preferredJobsiteLocationId) return -1;
    if (b.location_id === preferredJobsiteLocationId) return 1;
    return 0;
  });
};

// Function to merge new jobsite locations with existing ones
export const mergeJobsiteLocations = (
  existing: CompanyLocation[] | null,
  newLocations: CompanyLocation[],
): CompanyLocation[] => {
  let existingMap = new Map();
  if (existing) existingMap = new Map(existing.map((jobsite) => [jobsite.location_id, jobsite]));
  newLocations.forEach((jobsite) => {
    existingMap.set(jobsite.location_id, jobsite); // Overwrite or add new
  });
  return Array.from(existingMap.values());
};
